import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Dialog,
  IconButton,
  Avatar,
  CircularProgress,
  Breadcrumbs,
  Link,
} from "@mui/material";
import {
  DataGridPremium,
  GridToolbarContainer,
  GridToolbarExport,
} from "@mui/x-data-grid-premium";
import { toast } from "react-toastify";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import ResellerServices from "../../../services/reseller_services";
import classes from "./ResellerList.module.css";

const ResellerList = () => {
  const [resellers, setResellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReseller, setSelectedReseller] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    BusinessName: "",
    IsActive: true,
    IsDeleted: false,
    CreditPricePackages: [],
  });
  const [creditValidationErrors, setCreditValidationErrors] = useState({});
  const subscriptionPlanRowRef = useRef(null);

  // Load resellers on component mount
  useEffect(() => {
    loadResellers();
  }, []);

  const loadResellers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await ResellerServices.getAllResellerList(token);

      if (response.status === 200 && response.data?.ResellerList) {
        const formattedResellers = response.data.ResellerList.map(
          (reseller, index) => ({
            id: index + 1,
            resellerId: reseller.ResellerUserID._id,
            name: reseller.ResellerUserID.Name,
            email: reseller.ResellerUserID.Email,
            mobile: reseller.ResellerUserID.Mobile_Number,
            countryCode: reseller.ResellerUserID.CountryCode,
            businessName: reseller.BusinessName || "N/A",
            isActive: reseller.IsActive,
            isDeleted: reseller.IsDeleted,
            creditPackages: reseller.CreditPricePackages || [],
            originalData: reseller,
          })
        );
        setResellers(formattedResellers);
      } else {
        toast.error("Failed to load resellers", {
          position: "bottom-left",
          autoClose: 5000,
        });
      }
    } catch (error) {
      console.error("Error loading resellers:", error);
      toast.error("An error occurred while loading resellers", {
        position: "bottom-left",
        autoClose: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (reseller) => {
    setSelectedReseller(reseller);
    setFormData({
      BusinessName:
        reseller.businessName === "N/A" ? "" : reseller.businessName,
      IsActive: reseller.isActive,
      IsDeleted: reseller.isDeleted,
      CreditPricePackages: [...reseller.creditPackages],
    });
    setCreditValidationErrors({});
    setIsEditing(false);
    setOpenModal(true);
  };

  const handleEditToggle = () => {
    setIsEditing(!isEditing);
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCreditPackageChange = (index, field, value) => {
    // Check for duplicate credits if the field is Credits
    if (field === "Credits") {
      const existingCredits = formData.CreditPricePackages.map((pkg, i) =>
        i !== index ? pkg.Credits : null
      ).filter((credits) => credits !== null);

      const isDuplicate = existingCredits.includes(parseInt(value));

      if (isDuplicate) {
        toast.error(
          `Credit amount ${value} already exists in another package!`,
          {
            position: "bottom-left",
            autoClose: 3000,
          }
        );
        return;
      } else {
        // Clear validation error for this field
        setCreditValidationErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[`${index}-${field}`];
          return newErrors;
        });
      }
    }

    // Check for zero amounts
    if (field === "AmountWithoutTax" || field === "AmountWithTax") {
      const numValue = parseFloat(value);
      if (numValue === 0) {
        const fieldName =
          field === "AmountWithoutTax"
            ? "Amount Without Tax"
            : "Amount With Tax";
        const confirmed = window.confirm(
          `Are you sure you want to set ${fieldName} to 0 for Package ${
            index + 1
          }?`
        );

        if (confirmed) {
          // Clear validation error since user confirmed
          setCreditValidationErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors[`${index}-${field}`];
            return newErrors;
          });
        } else {
          // User cancelled, don't update the value
          return;
        }
      } else {
        setCreditValidationErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[`${index}-${field}`];
          return newErrors;
        });
      }
    }

    setFormData((prev) => ({
      ...prev,
      CreditPricePackages: prev.CreditPricePackages.map((pkg, i) =>
        i === index ? { ...pkg, [field]: value } : pkg
      ),
    }));
  };

  const addCreditPackage = () => {
    // Find the next available credit amount
    const existingCredits = formData.CreditPricePackages.map((pkg) =>
      parseInt(pkg.Credits)
    );
    let nextCredit = 1;
    while (existingCredits.includes(nextCredit)) {
      nextCredit++;
    }

    setFormData((prev) => ({
      ...prev,
      CreditPricePackages: [
        ...prev.CreditPricePackages,
        {
          Credits: nextCredit,
          AmountWithoutTax: 0,
          AmountWithTax: 0,
        },
      ],
    }));

    // Scroll to the latest added package after state update
    setTimeout(() => {
      if (subscriptionPlanRowRef.current) {
        subscriptionPlanRowRef.current.scrollTo({
          left: subscriptionPlanRowRef.current.scrollWidth,
          behavior: "smooth",
        });
      }
    }, 100);
  };

  const removeCreditPackage = (index) => {
    if (formData.CreditPricePackages.length > 1) {
      setFormData((prev) => ({
        ...prev,
        CreditPricePackages: prev.CreditPricePackages.filter(
          (_, i) => i !== index
        ),
      }));

      // Clear validation errors for the removed package
      setCreditValidationErrors((prev) => {
        const newErrors = { ...prev };
        Object.keys(newErrors).forEach((key) => {
          if (key.startsWith(`${index}-`)) {
            delete newErrors[key];
          }
        });
        return newErrors;
      });
    }
  };

  const handleSubmit = async () => {
    if (!selectedReseller) return;

    // Check for validation errors
    const hasValidationErrors = Object.keys(creditValidationErrors).length > 0;
    if (hasValidationErrors) {
      toast.error("Please fix validation errors before submitting", {
        position: "bottom-left",
        autoClose: 3000,
      });
      return;
    }

    // Check for zero amounts and show warning
    const hasZeroAmounts = formData.CreditPricePackages.some(
      (pkg) =>
        parseFloat(pkg.AmountWithoutTax) === 0 ||
        parseFloat(pkg.AmountWithTax) === 0
    );

    if (hasZeroAmounts) {
      const confirmed = window.confirm(
        "Some packages have zero amounts. Are you sure you want to proceed with the update?"
      );
      if (!confirmed) {
        return;
      }
    }

    setIsSubmitting(true);
    try {
      const token = localStorage.getItem("token");
      const updateData = {
        UserID: selectedReseller.resellerId,
        BusinessName: formData.BusinessName,
        IsActive: formData.IsActive,
        IsDeleted: formData.IsDeleted,
        CreditPricePackages: formData.CreditPricePackages.map((pkg) => ({
          Credits: parseInt(pkg.Credits),
          AmountWithoutTax: parseFloat(pkg.AmountWithoutTax),
          AmountWithTax: parseFloat(pkg.AmountWithTax),
          _id: pkg._id,
        })),
      };

      const response = await ResellerServices.updateResellerDetails(
        token,
        updateData
      );

      if (response.status === 200 && response.data?.message) {
        toast.success("Reseller details updated successfully!", {
          position: "bottom-left",
          autoClose: 5000,
        });
        setOpenModal(false);
        loadResellers(); // Reload the list
      } else {
        toast.error(response.error || "Failed to update reseller details", {
          position: "bottom-left",
          autoClose: 5000,
        });
      }
    } catch (error) {
      console.error("Error updating reseller:", error);
      toast.error("An error occurred while updating reseller details", {
        position: "bottom-left",
        autoClose: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedReseller(null);
    setIsEditing(false);
  };

  const hasChanges = () => {
    if (!selectedReseller) return false;

    // Check if basic fields have changed
    const basicFieldsChanged =
      formData.BusinessName !==
        (selectedReseller.businessName === "N/A"
          ? ""
          : selectedReseller.businessName) ||
      formData.IsActive !== selectedReseller.isActive ||
      formData.IsDeleted !== selectedReseller.isDeleted;

    // Check if credit packages have changed
    const originalPackages = selectedReseller.creditPackages || [];
    const currentPackages = formData.CreditPricePackages || [];

    if (originalPackages.length !== currentPackages.length) {
      return true;
    }

    const packagesChanged = originalPackages.some((originalPkg, index) => {
      const currentPkg = currentPackages[index];
      if (!currentPkg) return true;

      return (
        originalPkg.Credits !== parseInt(currentPkg.Credits) ||
        originalPkg.AmountWithoutTax !==
          parseFloat(currentPkg.AmountWithoutTax) ||
        originalPkg.AmountWithTax !== parseFloat(currentPkg.AmountWithTax)
      );
    });

    return basicFieldsChanged || packagesChanged;
  };

  const columns = [
    {
      field: "id",
      headerName: "#",
      width: 60,
    },
    {
      field: "name",
      headerName: "Name",
      width: 200,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Avatar sx={{ width: 32, height: 32 }}>
            {params.value?.charAt(0)?.toUpperCase() || "?"}
          </Avatar>
          {params.value || "-"}
        </Box>
      ),
    },
    {
      field: "email",
      headerName: "Email",
      width: 250,
      cellClassName: classes.centeredCell,
      renderCell: (params) => (
        <Typography variant="body2">{params.value || "-"}</Typography>
      ),
    },
    {
      field: "mobile",
      headerName: "Mobile",
      width: 150,
      cellClassName: classes.centeredCell,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value === -1
            ? "-"
            : `${params.row.countryCode} ${params.value}`}
        </Typography>
      ),
    },
    {
      field: "businessName",
      headerName: "Business Name",
      width: 200,
      cellClassName: classes.centeredCell,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value === "N/A" ? "-" : params.value}
        </Typography>
      ),
    },
    {
      field: "isActive",
      headerName: "Active",
      width: 80,
      cellClassName: classes.centeredCell,
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {params.value ? (
            <Box
              sx={{
                width: 20,
                height: 20,
                borderRadius: "50%",
                backgroundColor: "#e8f5e8",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#2e7d32",
                fontSize: "14px",
                fontWeight: "bold",
              }}
            >
              ✓
            </Box>
          ) : (
            <Box
              sx={{
                width: 20,
                height: 20,
                borderRadius: "50%",
                backgroundColor: "#ffebee",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#d32f2f",
                fontSize: "14px",
                fontWeight: "bold",
              }}
            >
              ✗
            </Box>
          )}
        </Box>
      ),
    },
    {
      field: "isDeleted",
      headerName: "Delete User",
      width: 100,
      cellClassName: classes.centeredCell,
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {params.value ? (
            <Box
              sx={{
                width: 20,
                height: 20,
                borderRadius: "50%",
                backgroundColor: "#e8f5e8",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#2e7d32",
                fontSize: "14px",
                fontWeight: "bold",
              }}
            >
              ✓
            </Box>
          ) : (
            <Box
              sx={{
                width: 20,
                height: 20,
                borderRadius: "50%",
                backgroundColor: "#ffebee",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#d32f2f",
                fontSize: "14px",
                fontWeight: "bold",
              }}
            >
              ✗
            </Box>
          )}
        </Box>
      ),
    },
    {
      field: "creditPackages",
      headerName: "Packages",
      width: 100,
      cellClassName: classes.centeredCell,
      renderCell: (params) => (
        <Typography variant="body2">{params.value.length}</Typography>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <IconButton
          onClick={() => handleViewDetails(params.row)}
          className={classes.actionButton}
        >
          <EditIcon />
        </IconButton>
      ),
    },
  ];

  function CustomToolbar() {
    return (
      <GridToolbarContainer
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
        }}
      >
        <GridToolbarExport
          printOptions={{ disableToolbarButton: true }}
          csvOptions={{ fileName: "ResellerList" }}
          excelOptions={{ fileName: "ResellerList" }}
        />
      </GridToolbarContainer>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Breadcrumbs aria-label="breadcrumb" sx={{ marginBottom: 2 }}>
        <Link underline="hover" color="inherit" href="/">
          Dashboard
        </Link>
        <Typography color="text.primary">Reseller Services</Typography>
        <Typography color="text.primary">Resellers</Typography>
      </Breadcrumbs>

      <DataGridPremium
        className={classes.dataGrid}
        slots={{
          toolbar: CustomToolbar,
        }}
        rows={resellers}
        columns={columns}
        loading={loading}
        disableRowSelectionOnClick
        pagination
        pageSizeOptions={[10, 25, 50, 100]}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 25 },
          },
        }}
      />

      {/* Reseller Details Modal */}
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        maxWidth="lg"
        fullWidth
        className={classes.modernDialog}
        PaperProps={{
          className: classes.modernDialogPaper,
        }}
      >
        {/* Custom Header */}
        <div className={classes.modernHeader}>
          <div className={classes.headerContent}>
            <div className={classes.resellerInfo}>
              <div className={classes.resellerAvatar}>
                {selectedReseller?.name?.charAt(0)?.toUpperCase() || "?"}
              </div>
              <div className={classes.resellerDetails}>
                <h2 className={classes.resellerName}>
                  {selectedReseller?.name || "-"}
                </h2>
                <p className={classes.resellerEmail}>
                  {selectedReseller?.email || "-"}
                </p>
              </div>
            </div>
            <button className={classes.closeButton} onClick={handleCloseModal}>
              <CloseIcon />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className={classes.tabContent}>
          {selectedReseller && (
            <div className={classes.viewContainer}>
              <div className={classes.infoGrid}>
                <div className={classes.basicInfoCard}>
                  <div className={classes.cardHeader}>
                    <div className={classes.headerTitle}>
                      <h3>Basic Information</h3>
                      <button
                        className={classes.editButton}
                        onClick={handleEditToggle}
                        title={isEditing ? "Cancel Edit" : "Edit Details"}
                      >
                        <EditIcon />
                        {isEditing ? "Cancel" : "Edit"}
                      </button>
                    </div>
                  </div>
                  <div className={classes.infoList}>
                    <div className={classes.twoColumnLayout}>
                      <div className={classes.infoItem}>
                        <span className={classes.infoLabel}>Name:</span>
                        <span className={classes.infoValue}>
                          {selectedReseller.name || "-"}
                        </span>
                      </div>
                      <div className={classes.infoItem}>
                        <span className={classes.infoLabel}>Email:</span>
                        <span className={classes.infoValue}>
                          {selectedReseller.email || "-"}
                        </span>
                      </div>
                      <div className={classes.infoItem}>
                        <span className={classes.infoLabel}>Mobile:</span>
                        <span className={classes.infoValue}>
                          {selectedReseller.mobile === -1
                            ? "-"
                            : `${selectedReseller.countryCode} ${selectedReseller.mobile}`}
                        </span>
                      </div>
                    </div>
                    <div className={classes.twoColumnLayout}>
                      <div className={classes.infoItem}>
                        <span className={classes.infoLabel}>
                          Business Name:
                        </span>
                        {isEditing ? (
                          <input
                            type="text"
                            className={classes.textInput}
                            value={formData.BusinessName}
                            onChange={(e) =>
                              handleInputChange("BusinessName", e.target.value)
                            }
                          />
                        ) : (
                          <span className={classes.infoValue}>
                            {formData.BusinessName || "-"}
                          </span>
                        )}
                      </div>
                      <div className={classes.infoItem}>
                        <span className={classes.infoLabel}>Active:</span>
                        {isEditing ? (
                          <label className={classes.switchLabel}>
                            <input
                              type="checkbox"
                              className={classes.switchInput}
                              checked={formData.IsActive}
                              onChange={(e) =>
                                handleInputChange("IsActive", e.target.checked)
                              }
                            />
                            <span className={classes.switchSlider}></span>
                            <span className={classes.switchText}>
                              {formData.IsActive ? "Yes" : "No"}
                            </span>
                          </label>
                        ) : (
                          <span
                            className={`${classes.statusChip} ${
                              formData.IsActive
                                ? classes.active
                                : classes.inactive
                            }`}
                          >
                            {formData.IsActive ? "Yes" : "No"}
                          </span>
                        )}
                      </div>
                      <div className={classes.infoItem}>
                        <span className={classes.infoLabel}>Delete User:</span>
                        {isEditing ? (
                          <label className={classes.switchLabel}>
                            <input
                              type="checkbox"
                              className={classes.switchInput}
                              checked={formData.IsDeleted}
                              onChange={(e) =>
                                handleInputChange("IsDeleted", e.target.checked)
                              }
                            />
                            <span className={classes.switchSlider}></span>
                            <span className={classes.switchText}>
                              {formData.IsDeleted ? "Yes" : "No"}
                            </span>
                          </label>
                        ) : (
                          <span
                            className={`${classes.statusChip} ${
                              formData.IsDeleted
                                ? classes.active
                                : classes.inactive
                            }`}
                          >
                            {formData.IsDeleted ? "Yes" : "No"}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Credit Packages Section */}
              <div className={classes.subscriptionSection}>
                <div className={classes.sectionHeader}>
                  <h3>Credit Packages</h3>
                  {isEditing && (
                    <button
                      className={classes.addPackageButton}
                      onClick={addCreditPackage}
                    >
                      <AddIcon />
                      Add Package
                    </button>
                  )}
                </div>
                <div
                  className={classes.subscriptionPlanRow}
                  ref={subscriptionPlanRowRef}
                >
                  {formData.CreditPricePackages.map((pkg, index) => (
                    <div key={index} className={classes.subscriptionPlanCard}>
                      <div className={classes.planHeader}>
                        <h4>Package {index + 1}</h4>
                        {isEditing &&
                          formData.CreditPricePackages.length > 1 && (
                            <button
                              className={classes.removePackageButton}
                              onClick={() => removeCreditPackage(index)}
                            >
                              <DeleteIcon />
                            </button>
                          )}
                      </div>
                      <div className={classes.plansList}>
                        <div className={classes.planItem}>
                          <div className={classes.planDetails}>
                            <div className={classes.planField}>
                              <span className={classes.fieldLabel}>
                                Credits:
                              </span>
                              {isEditing ? (
                                <input
                                  type="number"
                                  className={`${classes.numberInput} ${
                                    creditValidationErrors[`${index}-Credits`]
                                      ? classes.errorInput
                                      : ""
                                  }`}
                                  value={pkg.Credits}
                                  onChange={(e) =>
                                    handleCreditPackageChange(
                                      index,
                                      "Credits",
                                      e.target.value
                                    )
                                  }
                                />
                              ) : (
                                <span className={classes.fieldValue}>
                                  {pkg.Credits || "-"}
                                </span>
                              )}
                            </div>
                            <div className={classes.planField}>
                              <span className={classes.fieldLabel}>
                                Amount Without Tax:
                              </span>
                              {isEditing ? (
                                <input
                                  type="number"
                                  className={`${classes.numberInput} ${
                                    creditValidationErrors[
                                      `${index}-AmountWithoutTax`
                                    ]
                                      ? classes.errorInput
                                      : ""
                                  }`}
                                  value={pkg.AmountWithoutTax}
                                  onChange={(e) =>
                                    handleCreditPackageChange(
                                      index,
                                      "AmountWithoutTax",
                                      e.target.value
                                    )
                                  }
                                />
                              ) : (
                                <span className={classes.fieldValue}>
                                  {pkg.AmountWithoutTax
                                    ? `₹${pkg.AmountWithoutTax}`
                                    : "-"}
                                </span>
                              )}
                            </div>
                            <div className={classes.planField}>
                              <span className={classes.fieldLabel}>
                                Amount With Tax:
                              </span>
                              {isEditing ? (
                                <input
                                  type="number"
                                  className={`${classes.numberInput} ${
                                    creditValidationErrors[
                                      `${index}-AmountWithTax`
                                    ]
                                      ? classes.errorInput
                                      : ""
                                  }`}
                                  value={pkg.AmountWithTax}
                                  onChange={(e) =>
                                    handleCreditPackageChange(
                                      index,
                                      "AmountWithTax",
                                      e.target.value
                                    )
                                  }
                                />
                              ) : (
                                <span className={classes.fieldValue}>
                                  {pkg.AmountWithTax
                                    ? `₹${pkg.AmountWithTax}`
                                    : "-"}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Custom Footer */}
        <div className={classes.modernFooter}>
          <button className={classes.cancelButton} onClick={handleCloseModal}>
            Close
          </button>
          {isEditing && (
            <button
              className={`${classes.updateButton} ${
                isSubmitting || !hasChanges() ? classes.disabledButton : ""
              }`}
              onClick={handleSubmit}
              disabled={isSubmitting || !hasChanges()}
            >
              {isSubmitting ? (
                <>
                  <CircularProgress size={16} style={{ marginRight: "8px" }} />
                  Updating...
                </>
              ) : (
                "Update Details"
              )}
            </button>
          )}
        </div>
      </Dialog>
    </Box>
  );
};

export default ResellerList;
